import Immutable from 'immutable';
import flatMap from 'lodash/flatMap';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { v4 as uuidv4 } from 'uuid';

import { ROUTE } from 'app/constants/AdminBrowseConstants';
import {
  ADMIN_BROWSE_SECTION_TYPE,
  BROWSE_HOME_PAGE_STATUS,
  BROWSE_HOME_PAGE_SCHEMA_VERSION,
  DEFAULT_LINK_VALIDATION,
  LISTS_NUM_MIN_LIST_URLS,
  LISTS_HERO_NUM_MIN_LIST_URLS,
  VALIDATION_ERROR_MESSAGE
} from 'app/constants/admin/browse/home';
import { isValidUrl } from '.';

const deserializeBrowseHomePage = browseHomePage => {
  const { sections = [] } = browseHomePage;

  // remove legacy createdBy property
  if (browseHomePage.createdBy) {
    delete browseHomePage.createdBy;
  }

  // 1.1.0 - add listUrlId to listUrl objects
  sections.forEach(section => {
    const { listUrl = null, listUrls = [] } = section;

    if (listUrl) {
      listUrl.listUrlId = listUrl.listUrlId || uuidv4();
    }

    listUrls.forEach(nextListUrl => {
      nextListUrl.listUrlId = nextListUrl.listUrlId || uuidv4();
    });
  });

  // 1.2.0 - ensure all home pages have a status
  if (!browseHomePage.status) {
    browseHomePage.status = BROWSE_HOME_PAGE_STATUS.UNKNOWN;
  }

  // 1.3.0 - backfill heading on lists-hero sections
  sections.forEach(section => {
    if (section.sectionType === ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO && section.heading === undefined) {
      section.heading = '';
    }
  });

  browseHomePage.browseHomePageSchemaVersion = BROWSE_HOME_PAGE_SCHEMA_VERSION;

  return browseHomePage;
};

const hasAdminPermissions = user => {
  if (!user) {
    return false;
  }

  if (!Immutable.Map.isMap(user)) {
    throw new Error('user must be an Immutable.Map');
  }

  return user.get('permissions').indexOf('BROWSE_HOME_MANAGER') > -1;
};

const hasPreviewPermissions = user => {
  if (!user) {
    return false;
  }

  if (!Immutable.Map.isMap(user)) {
    throw new Error('user must be an Immutable.Map');
  }

  return user.get('roles').some(role => {
    return role === 'LIBRARY_STAFF' || role === 'SYS_ADMIN';
  });
};

const V2_LIST_URL_REGEX = /^https?:\/\/([^.]+).*.bibliocommons.com\/v2\/list\/display\/[0-9]+\/([0-9]+)/i;

const isValidListV2Url = urlCandidate => {
  return V2_LIST_URL_REGEX.test(urlCandidate);
};

const makeEmptyBrowseHomePage = () => {
  return {
    browseHomePageId: null,
    browseHomePageSchemaVersion: BROWSE_HOME_PAGE_SCHEMA_VERSION,
    name: 'Untitled Browse Home Page',
    sections: [],
    updatedAt: Date.now(),
    updatedBy: ''
  };
};

const makeEmptyListUrl = () => {
  return {
    listUrlId: uuidv4(),
    title: '',
    url: ''
  };
};

const makeEmptySection = sectionType => {
  switch (sectionType) {
    case ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES: {
      return {
        hasAlternativeBackground: false,
        heading: '',
        searchUrl: '',
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES
      };
    }
    case ADMIN_BROWSE_SECTION_TYPE.LINKS: {
      return {
        hasAlternativeBackground: false,
        heading: '',
        links: [],
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.LINKS
      };
    }
    case ADMIN_BROWSE_SECTION_TYPE.LISTS: {
      return {
        hasAlternativeBackground: false,
        heading: '',
        listUrls: [],
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.LISTS
      };
    }
    case ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO: {
      return {
        heading: '',
        listUrls: [],
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO
      };
    }
    case ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES: {
      return {
        hasAlternativeBackground: false,
        listUrl: makeEmptyListUrl(),
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES
      };
    }
    case ADMIN_BROWSE_SECTION_TYPE.NAVIGATION: {
      return {
        links: [],
        sectionId: uuidv4(),
        sectionType: ADMIN_BROWSE_SECTION_TYPE.NAVIGATION
      };
    }
    default:
      throw new Error(`Unkown sectionType: ${sectionType}`);
  }
};

const makeHomeCreatePageRoute = localeCode => {
  return ROUTE.ADMIN_BROWSE_HOME_CREATE.replace(':localeCode', localeCode);
};

const makeHomeEditPageRoute = (localeCode, browseHomePageId) => {
  return ROUTE.ADMIN_BROWSE_HOME_EDIT.replace(':localeCode', localeCode).replace(':browseHomePageId', browseHomePageId);
};

const makeHomePreviewPageRoute = (localeCode, browseHomePageId) => {
  return ROUTE.ADMIN_BROWSE_HOME_PREVIEW.replace(':localeCode', localeCode).replace(
    ':browseHomePageId',
    browseHomePageId
  );
};

const makeHomeListPageRoute = localeCode => {
  return ROUTE.ADMIN_BROWSE_HOME_LIST.replace(':localeCode?', localeCode);
};

const parseListUrl = listUrl => {
  try {
    const matches = V2_LIST_URL_REGEX.exec(listUrl);

    const subdomain = matches[1];
    const listId = matches[2];

    return { listId, subdomain };
  } catch {
    return null;
  }
};

const validateLink = linkCandidate => {
  const { label, url } = linkCandidate;

  const isValidLabel = Boolean(label);
  const isValidLinkUrl = isValidUrl(url);

  return {
    isValid: isValidLabel && isValidLinkUrl,
    errorByAttribute: {
      label: isValidLabel ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LABEL,
      url: isValidLinkUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_URL
    }
  };
};

const validateCatalogTitlesSection = catalogTitlesSection => {
  const { heading, searchUrl } = catalogTitlesSection;

  const isValidHeading = Boolean(heading);
  const isValidSearchUrl = isValidUrl(searchUrl);
  const isValid = isValidHeading && isValidSearchUrl;

  return {
    isValid,
    errorByAttribute: {
      heading: isValidHeading ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      searchUrl: isValidSearchUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_CATALOG_SEARCH_URL
    }
  };
};

const validateLinksSection = linksSection => {
  const { heading, links } = linksSection;

  const isValidHeading = Boolean(heading);

  const hasSomeLinks = links.length > 0;
  const createLinkValidation = hasSomeLinks ? DEFAULT_LINK_VALIDATION : validateLink({ label: '', url: '' });

  const isValid = isValidHeading && createLinkValidation.isValid;

  return {
    isValid,
    errorByAttribute: {
      heading: isValidHeading ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      links: {
        createLink: createLinkValidation
      }
    }
  };
};

const validateListsSection = listsSection => {
  const { heading, listUrls } = listsSection;

  const numUrlsToValidate = Math.max(listUrls.length, LISTS_NUM_MIN_LIST_URLS);
  const listUrlErrors = Array(numUrlsToValidate)
    .fill('')
    .map((_, index) => {
      return isValidListV2Url((listUrls[index] || makeEmptyListUrl()).url)
        ? ''
        : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL;
    });

  const isHeadingValid = Boolean(heading);
  const isListUrlsValid = listUrlErrors.every(error => !error);

  const isValid = isHeadingValid && isListUrlsValid;

  return {
    isValid,
    errorByAttribute: {
      heading: isHeadingValid ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      listUrls: listUrlErrors
    }
  };
};

const validateListsHeroSection = listsHeroSection => {
  const { heading, listUrls } = listsHeroSection;

  const numUrlsToValidate = Math.max(listUrls.length, LISTS_HERO_NUM_MIN_LIST_URLS);
  const listUrlErrors = Array(numUrlsToValidate)
    .fill('')
    .map((_, index) => {
      return isValidListV2Url((listUrls[index] || makeEmptyListUrl()).url)
        ? ''
        : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL;
    });

  const isHeadingValid = Boolean(heading);
  const isValid = isHeadingValid && listUrlErrors.every(error => !error);

  return {
    isValid,
    errorByAttribute: {
      heading: isHeadingValid ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      listUrls: listUrlErrors
    }
  };
};

const validateListTitlesSection = listTitlesSection => {
  const { listUrl } = listTitlesSection;

  const isValidListUrl = isValidListV2Url(listUrl.url);

  return {
    isValid: isValidListUrl,
    errorByAttribute: {
      listUrl: isValidListUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL
    }
  };
};

const validateNavigationSection = navigationSection => {
  const { links } = navigationSection;

  const hasSomeLinks = links.length > 0;
  const createLinkValidation = hasSomeLinks ? DEFAULT_LINK_VALIDATION : validateLink({ label: '', url: '' });
  const isValid = createLinkValidation.isValid;

  return {
    isValid,
    errorByAttribute: {
      links: {
        createLink: createLinkValidation
      }
    }
  };
};

const validateSection = section => {
  const { sectionType } = section;

  switch (sectionType) {
    case ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES:
      return validateCatalogTitlesSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LINKS:
      return validateLinksSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LISTS:
      return validateListsSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO:
      return validateListsHeroSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES:
      return validateListTitlesSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.NAVIGATION:
      return validateNavigationSection(section);
    default:
      throw new Error(`Unknown section type: ${sectionType}`);
  }
};

const validateBrowseHomePage = browseHomePage => {
  const { sections } = browseHomePage;

  const validationBySectionId = mapValues(keyBy(sections, 'sectionId'), validateSection);
  const isValid = flatMap(validationBySectionId, validation => validation.isValid).every(Boolean);

  return {
    isValid,
    validationBySectionId
  };
};

export {
  deserializeBrowseHomePage,
  hasAdminPermissions,
  hasPreviewPermissions,
  isValidListV2Url,
  makeEmptyBrowseHomePage,
  makeEmptyListUrl,
  makeEmptySection,
  makeHomeCreatePageRoute,
  makeHomeEditPageRoute,
  makeHomePreviewPageRoute,
  makeHomeListPageRoute,
  parseListUrl,
  validateBrowseHomePage,
  validateCatalogTitlesSection,
  validateLink,
  validateLinksSection,
  validateListsHeroSection,
  validateListsSection,
  validateListTitlesSection,
  validateNavigationSection
};
